// src/pages/Admin/PaymentsAdmin.jsx
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import {
  ArrowDownTrayIcon,
  XCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../supabaseClient';

const SEGMENTS = ['Deleted', 'Active', 'All'];

function statusBadge(status) {
  switch (status) {
    case 'Paid':
      return 'bg-green-100 text-green-800';
    case 'Pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'Refunded':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}
function typeBadge(type) {
  return type === 'Full' ? 'bg-purple-100 text-purple-800' : 'bg-indigo-100 text-indigo-800';
}

// ---------- Reads ----------
async function fetchCounts() {
  const { count: activeCount, error: activeErr } = await supabase
    .from('payments')
    .select('id', { count: 'exact', head: true })
    .eq('is_deleted', false);
  if (activeErr) throw activeErr;

  const { count: deletedCount, error: deletedErr } = await supabase
    .from('payments')
    .select('id', { count: 'exact', head: true })
    .eq('is_deleted', true);
  if (deletedErr) throw deletedErr;

  return { active: activeCount ?? 0, deleted: deletedCount ?? 0 };
}

/**
 * Robust search that works for both base-table and related-table columns:
 * - If no term → single LEFT join query (all rows)
 * - If term → run TWO queries and merge:
 *   A) base-only OR: invoice_number/method (LEFT join)
 *   B) student-name OR: students.full_name (INNER join)
 * This guarantees base matches are not excluded when a payment has no student.
 */
async function fetchPayments({ segment, q }) {
  const term = (q || '').trim();

  // Common select shape
  const baseFields = `
    id,
    student_id,
    invoice_number,
    amount,
    payment_method,
    payment_date,
    status,
    payment_type,
    is_deleted,
    notes,
    receipt_url,
    created_at,
    students:students ( id, full_name, email ),
    payment_enrollments ( enrollment_id ),
    payment_other_charges ( id )
  `;

  const applySegment = (query) => {
    if (segment === 'Active') return query.eq('is_deleted', false);
    if (segment === 'Deleted') return query.eq('is_deleted', true);
    return query;
  };

  // No search -> single LEFT join query (everything)
  if (!term) {
    let q1 = supabase
      .from('payments')
      .select(baseFields)
      .order('payment_date', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false });
    q1 = applySegment(q1);
    const { data, error } = await q1;
    if (error) throw error;

    return (data || []).map((p) => ({
      ...p,
      student_name: p?.students?.full_name ?? null,
      student_email: p?.students?.email ?? null,
      enrollmentsCount: Array.isArray(p?.payment_enrollments) ? p.payment_enrollments.length : 0,
      otherChargesCount: Array.isArray(p?.payment_other_charges) ? p.payment_other_charges.length : 0,
    }));
  }

  // With search term -> run two queries and merge:

  // A) Base-only search on invoice/method (LEFT join; catches rows w/o student)
  let baseQuery = supabase
    .from('payments')
    .select(baseFields)
    .or(`invoice_number.ilike.%${term}%`)
    .order('payment_date', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false });
  baseQuery = applySegment(baseQuery);
  const { data: baseData, error: baseErr } = await baseQuery;
  if (baseErr) throw baseErr;

  // B) Student name search (INNER join + foreignTable so PostgREST applies the filter)
  let studentQuery = supabase
    .from('payments')
    .select(
      baseFields.replace(
        'students:students ( id, full_name, email )',
        'students:students!inner ( id, full_name, email )'
      )
    )
    // NOTE: foreignTable MUST be the related table's name in your select
    .or(`full_name.ilike.%${term}%`, { foreignTable: 'students' })
    .order('payment_date', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false });
  studentQuery = applySegment(studentQuery);
  const { data: studentData, error: studentErr } = await studentQuery;
  if (studentErr) throw studentErr;

  // Merge unique by id (preserve base order)
  const map = new Map();
  (baseData || []).forEach((r) => map.set(r.id, r));
  (studentData || []).forEach((r) => { if (!map.has(r.id)) map.set(r.id, r); });
  const merged = Array.from(map.values());

  return merged.map((p) => ({
    ...p,
    student_name: p?.students?.full_name ?? null,
    student_email: p?.students?.email ?? null,
    enrollmentsCount: Array.isArray(p?.payment_enrollments) ? p.payment_enrollments.length : 0,
    otherChargesCount: Array.isArray(p?.payment_other_charges) ? p.payment_other_charges.length : 0,
  }));
}


// ---- Detail reads (drawer) ----
async function fetchPaymentDetails(id) {
  const { data, error } = await supabase
    .from('payments')
    .select(`
      id,
      student_id,
      invoice_number,
      amount,
      payment_method,
      payment_date,
      status,
      payment_type,
      is_deleted,
      notes,
      receipt_url,
      created_at,
      students:students ( id, full_name, email )
    `)
    .eq('id', id)
    .single();
  if (error) throw error;
  return {
    ...data,
    student_name: data?.students?.full_name ?? null,
    student_email: data?.students?.email ?? null,
  };
}

async function fetchPaymentEnrollments(id) {
  const { data, error } = await supabase
    .from('payment_enrollments')
    .select(`
      payment_id,
      enrollment_id,
      amount_applied,
      created_at,
      enrollments:enrollments (
        id,
        enrollment_date,
        status,
        subjects:subjects ( code, name ),
        intakes:intakes ( name )
      )
    `)
    .eq('payment_id', id)
    .order('created_at', { ascending: true });
  if (error) throw error;

  return (data || []).map((row) => ({
    enrollment_id: row.enrollment_id,
    amount_applied: row.amount_applied,
    created_at: row.created_at,
    status: row?.enrollments?.status ?? null,
    enrollment_date: row?.enrollments?.enrollment_date ?? null,
    subject_code: row?.enrollments?.subjects?.code ?? '—',
    subject_name: row?.enrollments?.subjects?.name ?? '—',
    intake_name: row?.enrollments?.intakes?.name ?? '—',
  }));
}

async function fetchOtherCharges(id) {
  const { data, error } = await supabase
    .from('payment_other_charges')
    .select(`id, category, description, amount, created_at`)
    .eq('payment_id', id)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

// ---------- Writes ----------
async function restorePaymentsByIds(ids) {
  const { error } = await supabase.from('payments').update({ is_deleted: false }).in('id', ids);
  if (error) throw error;
  return { restored: ids.length };
}

async function hardDeletePaymentsByIds(ids) {
  const { error: linkErr } = await supabase.from('payment_enrollments').delete().in('payment_id', ids);
  if (linkErr) throw linkErr;
  const { error: payErr } = await supabase.from('payments').delete().in('id', ids);
  if (payErr) throw payErr;
  return { deleted: ids.length };
}

// ---- CSV helper ----
function toCSV(rows) {
  const headers = [
    'invoice_number',
    'student_name',
    'student_email',
    'amount',
    'payment_date',
    'payment_method',
    'payment_type',
    'status',
    'is_deleted',
    'enrollments_count',
    'other_charges_count',
    'created_at',
  ];
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(',')];
  rows.forEach((r) => {
    lines.push(
      [
        escape(r.invoice_number),
        escape(r.student_name),
        escape(r.student_email),
        escape(Number(r.amount).toFixed(2)),
        escape(
          r.payment_date
            ? new Date(r.payment_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
            : ''
        ),
        escape(r.payment_method),
        escape(r.payment_type),
        escape(r.status),
        escape(r.is_deleted ? 'true' : 'false'),
        escape(r.enrollmentsCount),
        escape(r.otherChargesCount),
        escape(r.created_at ? new Date(r.created_at).toISOString() : ''),
      ].join(',')
    );
  });
  return new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
}
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export default function PaymentsAdmin() {
  const qc = useQueryClient();

  const [segment, setSegment] = useState('Deleted');
  const [selection, setSelection] = useState(new Set());
  const [search, setSearch] = useState('');

  // Drawer
  const [drawerId, setDrawerId] = useState(null);
  const [drawerTab, setDrawerTab] = useState('Summary');

  // Modals
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreIds, setRestoreIds] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIds, setDeleteIds] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState('');

  // Counts
  const { data: counts = { active: 0, deleted: 0 } } = useQuery({
    queryKey: ['admin_payments_counts'],
    queryFn: fetchCounts,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  // List (live search via queryKey)
  const {
    data: rows = [],
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['admin_payments_list', segment, search],
    queryFn: () => fetchPayments({ segment, q: search }),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  // Detail queries
  const { data: detail } = useQuery({
    queryKey: ['admin_payment_detail', drawerId],
    queryFn: () => fetchPaymentDetails(drawerId),
    enabled: !!drawerId,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
  const { data: detailEnrollments = [] } = useQuery({
    queryKey: ['admin_payment_enrollments', drawerId],
    queryFn: () => fetchPaymentEnrollments(drawerId),
    enabled: !!drawerId && drawerTab === 'Enrollments',
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
  const { data: detailOtherCharges = [] } = useQuery({
    queryKey: ['admin_payment_other_charges', drawerId],
    queryFn: () => fetchOtherCharges(drawerId),
    enabled: !!drawerId && drawerTab === 'Other Charges',
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  // Restore
  const restoreMutation = useMutation({
    mutationFn: (ids) => restorePaymentsByIds(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin_payments_list'] });
      qc.invalidateQueries({ queryKey: ['admin_payments_counts'] });
      qc.invalidateQueries({ queryKey: ['payments_list'] });
      setShowRestoreModal(false);
      setRestoreIds([]);
      setSelection(new Set());
      if (drawerId && restoreIds.includes(drawerId)) {
        qc.invalidateQueries({ queryKey: ['admin_payment_detail', drawerId] });
      }
    },
    onError: (err) => alert(`Failed to restore: ${err?.message || err}`),
  });

  // Hard Delete
  const hardDeleteMutation = useMutation({
    mutationFn: (ids) => hardDeletePaymentsByIds(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin_payments_list'] });
      qc.invalidateQueries({ queryKey: ['admin_payments_counts'] });
      setShowDeleteModal(false);
      setDeleteIds([]);
      setConfirmDelete('');
      setSelection(new Set());
      if (drawerId && deleteIds.includes(drawerId)) setDrawerId(null);
    },
    onError: (err) => alert(`Failed to delete: ${err?.message || err}`),
  });

  const columns = useMemo(
    () => [
      { key: 'invoice', label: 'Invoice' },
      { key: 'student', label: 'Student' },
      { key: 'amount', label: 'Amount' },
      { key: 'date', label: 'Date' },
      { key: 'method', label: 'Method' },
      { key: 'type', label: 'Type' },
      { key: 'status', label: 'Status' },
      { key: 'linked', label: 'Linked' },
      { key: 'deleted', label: 'Deleted?' },
      { key: 'actions', label: 'Actions' },
    ],
    []
  );

  const allSelected = selection.size > 0 && selection.size === rows.length;
  const anyDeletedSelected = useMemo(
    () => rows.some((r) => selection.has(r.id) && r.is_deleted),
    [rows, selection]
  );

  const openDrawer = (id) => {
    setDrawerId(id);
    setDrawerTab('Summary');
  };

  // Export current rows
  const onExportCSV = () => {
    if (!rows || rows.length === 0) return;
    const blob = toCSV(rows);
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const suffix = segment === 'All' ? 'all' : segment === 'Active' ? 'active' : 'deleted';
    downloadBlob(blob, `payments-${suffix}-${stamp}.csv`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Payments Master Administration</h1>
          <p className="text-sm text-gray-500">Review, restore, permanently delete, or export payments.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            className="flex items-center gap-2"
            onClick={onExportCSV}
            disabled={isLoading || (rows?.length ?? 0) === 0}
            title={rows.length ? 'Export current rows to CSV' : 'Nothing to export'}
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-3">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-semibold text-gray-900">{counts.active}</div>
            </div>
            <div className="text-green-600">
              <CheckCircleIcon className="h-6 w-6" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500">Deleted</div>
              <div className="text-2xl font-semibold text-gray-900">{counts.deleted}</div>
            </div>
            <div className="text-gray-600">
              <XCircleIcon className="h-8 w-8 text-rose-500 opacity-80" />
            </div>
          </div>
        </Card>
      </div>

      {/* Filter Bar (live) */}
      <Card>
        <div className="flex items-center justify-between">
          {/* Segment control */}
          <div className="inline-flex rounded-md shadow-sm border border-gray-200 overflow-hidden">
            {SEGMENTS.map((s) => (
              <button
                key={s}
                onClick={() => {
                  setSegment(s);
                  setSelection(new Set());
                }}
                className={`px-3 py-1.5 text-sm ${
                  segment === s ? 'bg-gray-800 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          {/* Live search input */}
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search invoice / student name"
            className="ml-3 flex-1 max-w-md px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300"
          />
        </div>
      </Card>

      {/* Bulk actions bar */}
      {selection.size > 0 && (
        <Card>
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">{selection.size} selected</div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                disabled={!anyDeletedSelected}
                onClick={() => {
                  const ids = rows.filter((r) => selection.has(r.id) && r.is_deleted).map((r) => r.id);
                  setRestoreIds(ids);
                  setShowRestoreModal(true);
                }}
              >
                Restore
              </Button>
              <Button
                variant="destructive"
                disabled={!anyDeletedSelected}
                onClick={() => {
                  const ids = rows.filter((r) => selection.has(r.id) && r.is_deleted).map((r) => r.id);
                  setDeleteIds(ids);
                  setShowDeleteModal(true);
                }}
              >
                Hard Delete
              </Button>
              <Button variant="ghost" onClick={() => setSelection(new Set())}>
                Clear
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Results Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={allSelected}
                    onChange={(e) => {
                      if (e.target.checked) setSelection(new Set(rows.map((r) => r.id)));
                      else setSelection(new Set());
                    }}
                  />
                </th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {isLoading && rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 1} className="px-6 py-12 text-center text-gray-500">
                    Loading payments…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 1} className="px-6 py-12 text-center text-gray-500">
                    No payments to display.
                  </td>
                </tr>
              ) : (
                rows.map((p) => {
                  const selected = selection.has(p.id);
                  return (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={selected}
                          onChange={(e) => {
                            const next = new Set(selection);
                            if (e.target.checked) next.add(p.id);
                            else next.delete(p.id);
                            setSelection(next);
                          }}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-900">
                        {p.invoice_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{p.student_name || '—'}</div>
                        {p.student_email && <div className="text-xs text-gray-500">{p.student_email}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        RM{Number(p.amount).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {p.payment_date
                          ? new Date(p.payment_date).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{p.payment_method}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${typeBadge(p.payment_type)}`}>
                          {p.payment_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusBadge(p.status)}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-800">
                            Enrollments · {p.enrollmentsCount}
                          </span>
                          <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-800">
                            Other · {p.otherChargesCount}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {p.is_deleted ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            Yes
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-50 text-green-700">
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-3">
                          <button className="text-blue-600 hover:text-blue-800" onClick={() => openDrawer(p.id)}>
                            Details
                          </button>
                          {p.is_deleted ? (
                            <>
                              <button
                                className="text-indigo-600 hover:text-indigo-800"
                                onClick={() => {
                                  setRestoreIds([p.id]);
                                  setShowRestoreModal(true);
                                }}
                              >
                                Restore
                              </button>
                              <button
                                className="text-red-600 hover:text-red-800"
                                onClick={() => {
                                  setDeleteIds([p.id]);
                                  setShowDeleteModal(true);
                                }}
                              >
                                Delete
                              </button>
                            </>
                          ) : (
                            <button className="text-gray-400 cursor-not-allowed" disabled>
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {isFetching && rows.length > 0 && <div className="px-4 py-2 text-xs text-gray-400">Refreshing…</div>}
      </Card>

      {/* ----- Drawer (Details) ----- */}
      {drawerId && (
        <div className="fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-black/30" onClick={() => setDrawerId(null)} />
          <div className="ml-auto h-full w-full max-w-xl bg-white shadow-xl z-50 flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-xs text-gray-500">Invoice</div>
                <h2 className="text-lg font-semibold truncate">{detail?.invoice_number || '—'}</h2>
                <div className="text-sm text-gray-500 truncate">{detail?.student_name || '—'}</div>
              </div>
              <button onClick={() => setDrawerId(null)}>
                <XMarkIcon className="h-6 w-6 text-gray-500" />
              </button>
            </div>

            {/* Drawer tabs */}
            <div className="px-4 pt-3">
              <div className="inline-flex rounded-md shadow-sm border border-gray-200 overflow-hidden">
                {['Summary', 'Enrollments', 'Other Charges'].map((t) => (
                  <button
                    key={t}
                    onClick={() => setDrawerTab(t)}
                    className={`px-3 py-1.5 text-sm ${
                      drawerTab === t ? 'bg-gray-800 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 space-y-6 overflow-y-auto">
              {/* Summary */}
              {drawerTab === 'Summary' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-gray-500">Amount</div>
                      <div className="text-sm text-gray-900">
                        {detail ? `RM${Number(detail.amount).toFixed(2)}` : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Date</div>
                      <div className="text-sm text-gray-900">
                        {detail?.payment_date
                          ? new Date(detail.payment_date).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })
                          : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Method</div>
                      <div className="text-sm text-gray-900">{detail?.payment_method || '—'}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div>
                        <div className="text-xs text-gray-500">Type</div>
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${typeBadge(
                            detail?.payment_type
                          )}`}
                        >
                          {detail?.payment_type || '—'}
                        </span>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Status</div>
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusBadge(
                            detail?.status
                          )}`}
                        >
                          {detail?.status || '—'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Created</div>
                      <div className="text-sm text-gray-900">
                        {detail?.created_at ? new Date(detail.created_at).toLocaleString() : '—'}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-500">Notes</div>
                    <div className="text-sm text-gray-900 whitespace-pre-wrap">{detail?.notes || '—'}</div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-500">Receipt</div>
                    {detail?.receipt_url ? (
                      <a
                        className="text-blue-600 hover:text-blue-800 text-sm underline break-all"
                        href={detail.receipt_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open receipt
                      </a>
                    ) : (
                      <div className="text-sm text-gray-900">—</div>
                    )}
                  </div>
                </div>
              )}

              {/* Enrollments */}
              {drawerTab === 'Enrollments' && (
                <Card>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Intake</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Applied</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Linked</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {detailEnrollments.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                              No enrollment links for this payment.
                            </td>
                          </tr>
                        ) : (
                          detailEnrollments.map((row) => (
                            <tr key={row.enrollment_id} className="hover:bg-gray-50">
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {row.subject_code} — {row.subject_name}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">{row.intake_name}</td>
                              <td className="px-4 py-2 text-sm">
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                  {row.status || '—'}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                RM{Number(row.amount_applied).toFixed(2)}
                              </td>
                              <td className="px-4 py-2 text-xs text-gray-500">
                                {row.enrollment_date
                                  ? new Date(row.enrollment_date).toLocaleDateString('en-GB', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                    })
                                  : '—'}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}

              {/* Other Charges */}
              {drawerTab === 'Other Charges' && (
                <Card>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {detailOtherCharges.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                              No other charges for this payment.
                            </td>
                          </tr>
                        ) : (
                          detailOtherCharges.map((oc) => (
                            <tr key={oc.id} className="hover:bg-gray-50">
                              <td className="px-4 py-2 text-sm text-gray-900">{oc.category}</td>
                              <td className="px-4 py-2 text-sm text-gray-900 whitespace-pre-wrap">{oc.description}</td>
                              <td className="px-4 py-2 text-sm text-gray-900">RM{Number(oc.amount).toFixed(2)}</td>
                              <td className="px-4 py-2 text-xs text-gray-500">{new Date(oc.created_at).toLocaleString()}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </div>

            {/* Drawer footer */}
            <div className="p-4 border-t flex items-center justify-end gap-2">
              {detail?.is_deleted ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setRestoreIds([drawerId]);
                      setShowRestoreModal(true);
                    }}
                  >
                    Restore
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      setDeleteIds([drawerId]);
                      setShowDeleteModal(true);
                    }}
                  >
                    Hard Delete
                  </Button>
                </>
              ) : (
                <span className="text-xs text-gray-400">Active payment</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Restore Modal */}
      {showRestoreModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/30" onClick={() => setShowRestoreModal(false)} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-2">Restore payment{restoreIds.length > 1 ? 's' : ''}?</h3>
            <p className="text-sm text-gray-600 mb-4">
              {restoreIds.length > 1
                ? `You are about to restore ${restoreIds.length} payments.`
                : 'This payment will reappear in student and payment lists.'}
            </p>
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setShowRestoreModal(false)}>Cancel</Button>
              <Button onClick={() => restoreMutation.mutate(restoreIds)} disabled={restoreMutation.isLoading}>
                {restoreMutation.isLoading ? 'Restoring…' : 'Restore'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Hard Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/30" onClick={() => setShowDeleteModal(false)} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-3 mb-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold">Permanently delete payment{deleteIds.length > 1 ? 's' : ''}?</h3>
                <p className="text-sm text-gray-600">
                  Enrollment links will be removed first. Other charges will be deleted automatically.
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <label className="text-sm text-gray-700">
              Type <span className="font-mono">DELETE</span> to confirm
            </label>
            <input
              className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
              value={confirmDelete}
              onChange={(e) => setConfirmDelete(e.target.value)}
              placeholder="DELETE"
            />
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
              <Button
                variant="destructive"
                disabled={confirmDelete !== 'DELETE' || hardDeleteMutation.isLoading}
                onClick={() => hardDeleteMutation.mutate(deleteIds)}
              >
                {hardDeleteMutation.isLoading ? 'Deleting…' : 'Hard Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
